import { useCallback, useEffect, useState } from "react";
import { getApiHeaders } from "../utils/api";

/**
 * Custom hook for fetching and managing equipment and asset data
 * @param {string} apiBaseUrl - Base URL for the API
 * @returns {Object} { equipmentData, assetsData, loading, error, refetch }
 */
function useEquipmentData(apiBaseUrl) {
  const [equipmentData, setEquipmentData] = useState([]);
  const [assetsData, setAssetsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch equipment metadata from backend
  const fetchEquipmentMetadata = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all assets and their equipment
      const assetsResponse = await fetch(`${apiBaseUrl}/assets`, {
        headers: getApiHeaders(),
      });
      if (!assetsResponse.ok) {
        throw new Error("Failed to fetch assets");
      }
      const assets = await assetsResponse.json();

      // For each asset, fetch equipment with full metadata
      const allEquipment = [];
      const assetsArray = [];

      for (const assetId of Object.keys(assets)) {
        const equipmentResponse = await fetch(
          `${apiBaseUrl}/assets/${assetId}/equipment`,
          {
            headers: getApiHeaders(),
          }
        );
        if (!equipmentResponse.ok) {
          console.error(`Failed to fetch equipment for asset ${assetId}`);
          continue;
        }
        const assetEquipment = await equipmentResponse.json();

        // Store asset data
        assetsArray.push({
          id: assetId,
          type: assetEquipment.asset_type,
          industry: assetEquipment.industry,
          name: assets[assetId].name || assetId,
          description:
            assets[assetId].description || "No description available",
          equipmentCount: assetEquipment.equipment.length,
        });

        // Add each equipment instance with asset context
        for (const equip of assetEquipment.equipment) {
          allEquipment.push({
            // Unique key combining asset and instance
            id: `${assetId}.${equip.instance_id}`,
            assetId: assetId,
            assetType: assetEquipment.asset_type,
            instanceId: equip.instance_id,
            typeName: equip.type_name,
            // Metadata from backend - use instance name if provided, fallback to class name
            name: equip.name || equip.class_display_name,
            type: equip.type,
            industry: equip.industry,
            // Use instance description if provided, fallback to class description
            description: equip.description || equip.class_description,
            classDisplayName: equip.class_display_name,
            classDescription: equip.class_description,
            configuration: equip.configuration,
            // Parameters with qualified tag names
            parameters: equip.parameters.map((p) => ({
              name: p.name, // Parameter name only (for WebSocket endpoint)
              displayName: p.display_name,
              unit: p.unit,
              tag: p.tag, // Full qualified tag: asset_id.instance_id.param (for data lookup)
            })),
          });
        }
      }

      setEquipmentData(allEquipment);
      setAssetsData(assetsArray);
    } catch (err) {
      console.error("Error fetching equipment metadata:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  // Fetch metadata on mount
  useEffect(() => {
    fetchEquipmentMetadata();
  }, [fetchEquipmentMetadata]);

  return {
    equipmentData,
    assetsData,
    loading,
    error,
    refetch: fetchEquipmentMetadata,
  };
}

export default useEquipmentData;
